/**
 * The customers a commercial follows, on their sheet: removed one by one, or added by searching
 * the customer list. Moving a whole portfolio to somebody else stays on the list's « Réaffecter »,
 * where it is one statement on the server.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction, useDebounced, useResource } from "@hardauto/core-data";
import { Button, Input, useToast } from "@hardauto/core-ui";

import type { ClientsApi, SalesRep } from "../../api";
import { NAMESPACE } from "../../i18n";

export function RepClientsSection({
  api,
  rep,
  mayAssign,
  onChanged,
}: {
  api: ClientsApi;
  rep: SalesRep;
  mayAssign: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const [term, setTerm] = useState("");
  const search = useDebounced(term, 250);

  const portfolioFetcher = useCallback(() => api.portfolio(rep.code), [api, rep.code]);
  const portfolio = useResource(portfolioFetcher);
  const mine = new Set((portfolio.data?.items ?? []).map((entry) => entry.client.id));

  const foundFetcher = useCallback(
    () =>
      search.trim().length >= 2
        ? api.listClients({ search: search.trim(), limit: 10 })
        : Promise.resolve({ items: [], total: 0, next_cursor: null }),
    [api, search],
  );
  const found = useResource(foundFetcher);

  function move(clientId: string, to: string | null) {
    void action.run(clientId, async () => {
      try {
        await api.assignClients([clientId], to);
        portfolio.reload();
        found.reload();
        onChanged();
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      }
    });
  }

  return (
    <section className="hardauto-rep-section" aria-label={t("salesReps.clients.title")}>
      <h3>{t("salesReps.clients.title", { count: portfolio.data?.items.length ?? 0 })}</h3>
      {(portfolio.data?.items ?? []).length === 0 ? (
        <p className="hardauto-cell__muted">{t("salesReps.clients.none")}</p>
      ) : (
        <ul className="hardauto-rep-clients">
          {(portfolio.data?.items ?? []).map((entry) => (
            <li key={entry.client.id}>
              <span className="hardauto-ref">{entry.client.code}</span> {entry.client.company_name}
              {mayAssign ? (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t("salesReps.clients.removeOne", { code: entry.client.code })}
                  busy={action.isBusy(entry.client.id)}
                  busyLabel={t("salesReps.clients.remove")}
                  onClick={() => move(entry.client.id, null)}
                >
                  {t("salesReps.clients.remove")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {mayAssign ? (
        <>
          <Input
            type="search"
            aria-label={t("salesReps.clients.searchLabel")}
            placeholder={t("salesReps.clients.searchPlaceholder")}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <ul className="hardauto-rep-clients">
            {(found.data?.items ?? [])
              .filter((row) => !mine.has(row.client.id))
              .map((row) => (
                <li key={row.client.id}>
                  <span className="hardauto-ref">{row.client.code}</span> {row.client.company_name}
                  {row.sales_rep_name ? (
                    <span className="hardauto-cell__muted">
                      {" "}
                      {t("salesReps.clients.followedBy", { name: row.sales_rep_name })}
                    </span>
                  ) : null}
                  <Button
                    size="sm"
                    aria-label={t("salesReps.clients.addOne", { code: row.client.code })}
                    busy={action.isBusy(row.client.id)}
                    busyLabel={t("salesReps.clients.add")}
                    onClick={() => move(row.client.id, rep.code)}
                  >
                    {t("salesReps.clients.add")}
                  </Button>
                </li>
              ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
